import "@/App.css";
import { useMemo } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Generator from "@/pages/Generator";
import AffiliateManager from "@/pages/AffiliateManager";
import Analytics from "@/pages/Analytics";
import CompliancePolicies from "@/pages/CompliancePolicies";
import Surveys from "@/pages/Surveys";
import SurveyForm from "@/pages/SurveyForm";
import Schedules from "@/pages/Schedules";
import Connections from "@/pages/Connections";
import Leads from "@/pages/Leads";
import Dmarc from "@/pages/Dmarc";
import AuthCallback from "@/pages/AuthCallback";
import { AuthProvider } from "@/lib/auth";

/**
 * CRITICAL: session_id lives in the URL fragment (window.location.hash) so we
 * detect it synchronously during render — not inside useEffect (too late).
 * Without this, the global AuthProvider's /auth/me check would race the
 * session exchange and 401.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Visitor-facing survey page — rendered WITHOUT the app Layout */}
      <Route
        path="/survey/:adSetId/:platform/:variantId"
        element={<SurveyForm />}
      />
      {/* Everything else in the admin Layout */}
      <Route
        path="/*"
        element={
          <Layout>
            <Routes>
              <Route path="/" element={<Generator />} />
              <Route path="/links" element={<AffiliateManager />} />
              <Route path="/compliance" element={<CompliancePolicies />} />
              <Route path="/surveys" element={<Surveys />} />
              <Route path="/schedules" element={<Schedules />} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/dmarc" element={<Dmarc />} />
              <Route path="/analytics" element={<Analytics />} />
            </Routes>
          </Layout>
        }
      />
    </Routes>
  );
}

function App() {
  const toastOptions = useMemo(
    () => ({
      style: {
        background: "#0a0a0a",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 0,
        color: "#fff",
        fontFamily: "JetBrains Mono",
      },
    }),
    [],
  );
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
      <Toaster position="bottom-right" theme="dark" toastOptions={toastOptions} />
    </BrowserRouter>
  );
}

export default App;
